'use client';

import { useState } from 'react';

import { AdaptiveDialog } from '@naumu/ui/components/adaptive-dialog';
import { Button } from '@naumu/ui/components/button';
import { Switch } from '@naumu/ui/components/switch';

import { getAnalyticsConsent, setAnalyticsConsent } from './analytics-consent';

interface CookiePreferencesDialogProps {
	open: boolean;
	onClose: () => void;
	/** Called after Save wrote the decision, so the banner can hide itself. */
	onSaved?: () => void;
}

interface PreferenceRowProps {
	title: string;
	description: string;
	checked: boolean;
	locked?: boolean;
	onChange?: (checked: boolean) => void;
}

const PreferenceRow = ({ title, description, checked, locked, onChange }: PreferenceRowProps) => (
	<div className="flex items-start justify-between gap-6 border-b border-solid border-border-muted py-5">
		<div className="min-w-0">
			<p className="text-[15px] font-semibold leading-tight text-neutral">{title}</p>
			<p className="mt-1 text-[13px] leading-relaxed text-neutral-muted">{description}</p>
		</div>
		<Switch
			checked={checked}
			disabled={locked}
			onChange={(next) => onChange?.(next)}
			aria-label={title}
			className="mt-0.5"
		/>
	</div>
);

/**
 * Granular cookie-preferences dialog (mirrors the ChatGPT-style layout):
 * a locked "strictly necessary" row plus one toggle per consent category.
 *
 * Only one consent category exists today (product analytics), so Save calls
 * `setAnalyticsConsent()` - the single writer that keeps PostHog and Google
 * Consent Mode in sync (see gtm/CONSENT.md).
 */
export const CookiePreferencesDialog = ({ open, onClose, onSaved }: CookiePreferencesDialogProps) => {
	const [analytics, setAnalytics] = useState(() => getAnalyticsConsent() === 'granted');

	const save = () => {
		setAnalyticsConsent(analytics);
		onSaved?.();
		onClose();
	};

	return (
		<AdaptiveDialog
			open={open}
			onClose={onClose}
			title="Cookie preferences"
			desktopClassName="w-full max-w-[640px]">
			<div className="p-6 pt-8 sm:p-8">
				<h2 className="text-[28px] font-semibold leading-tight text-neutral">Cookie preferences</h2>
				<p className="mt-3 text-[15px] leading-relaxed text-neutral-muted">
					Websites and apps use cookies and other identifiers to store and retrieve
					information on your device. Use the controls below to manage your
					preferences. You can change them anytime.{' '}
					<a
						href="/legal/cookie-policy"
						className="text-neutral underline underline-offset-4 hover:no-underline">
						Learn more
					</a>
				</p>

				<div className="mt-6 border-t border-solid border-border-muted">
					<PreferenceRow
						title="Strictly necessary"
						description="Required for the site to work and can't be turned off. They support essential functions like security, user authentication, and customer support."
						checked
						locked
					/>
					<PreferenceRow
						title="Product analytics"
						description="Help us understand how Naumu is used so we can improve it. Nothing is collected unless you turn this on."
						checked={analytics}
						onChange={setAnalytics}
					/>
				</div>

				<Button variant="solid-strong" size="lg" className="mt-6 w-full" onClick={save}>
					Save
				</Button>
			</div>
		</AdaptiveDialog>
	);
};
